import React, {ReactElement, useState, useContext} from 'react';
import {
  Avatar,
  Button,
  IconButton,
  PersonIcon,
  Stack
} from '@customink/pigment-react';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useTheme} from '@mui/material/styles';
import AccordionMenu from '../AccordionMenu';
import AccordionMenuItem from '../AccordionMenu/AccordionMenuItem';
import {MobileContext} from '../../containers/MobileProvider';
import {useFetchUserQuery} from '../../api/userApi';
import {
  selectAdmin,
  selectAuthorized,
  selectLoggedIn
} from '../../slices/userSlice';
import services from '../../api/remoteServices';
import {useAppSelector} from '../../hooks';
import './index.scss';

export interface Props {
  storeSlug: string;
}

const AccountDropdown: React.FC<Props> = ({storeSlug}: Props): ReactElement => {
  useFetchUserQuery({storeSlug});
  const userEmail = useAppSelector(state => state.user.email);
  const loggedIn = useAppSelector(selectLoggedIn);
  const admin = useAppSelector(selectAdmin);
  const authorized = useAppSelector(selectAuthorized);
  const [menuExpanded, setMenuExpanded] = useState<boolean>(false);
  const theme = useTheme();
  const hideUsername = useMediaQuery(theme.breakpoints.down(1282));
  const mobile = useContext(MobileContext);

  const handleMenuBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setMenuExpanded(false);
    }
  };

  const updateMenuItemExpanded = () => {
    setMenuExpanded(!menuExpanded);
  };

  const UserAvatar = (): ReactElement => {
    const handleLogin = () => {
      window.location.href = `${
        services.stores
      }/stores/new?redirect_url=${encodeURI(window.location.href)}`;
    };
    return loggedIn && userEmail ? (
      <Avatar data={userEmail} size="large" />
    ) : (
      <IconButton
        className="menu-profile"
        size="medium"
        aria-label="Person"
        onClick={handleLogin}
        onKeyDown={handleLogin}>
        <PersonIcon fontSize="inherit" className="menu-profile-image" />
      </IconButton>
    );
  };
  const containerClassnames = `accountDropdown-container${
    mobile ? ' mobile' : ''
  }`;

  return loggedIn ? (
    <Stack
      alignItems={mobile ? 'flex-start' : 'center'}
      justifyContent="space-between"
      direction="row"
      className={containerClassnames}>
      {!hideUsername ? <UserAvatar /> : null}
      <div className="accountDropdown" onBlur={handleMenuBlur}>
        <AccordionMenu title="">
          <AccordionMenuItem
            title={`Hi, ${userEmail || '[username]'}!`}
            subtitle="My Account"
            menuExpanded={menuExpanded}
            onToggle={updateMenuItemExpanded}
            hideTitle={hideUsername}
            userEmail={userEmail && userEmail}>
            <Button
              className="yourAccount"
              href={`${services.homePage}/profiles/designs`}
              target="_blank"
              rel="noreferrer"
              role="link">
              Your Account
            </Button>
            {(admin || authorized) && (
              <Button
                className="administrate"
                href={`${services.stores}/${storeSlug}/manage/store_products`}
                target="_blank"
                rel="noreferrer"
                role="link">
                Administrate
              </Button>
            )}
            <Button
              className="logOut"
              href={`${services.homePage}/profiles/users/sign_out`}
              target="_blank"
              rel="noreferrer"
              role="link">
              Log Out
            </Button>
          </AccordionMenuItem>
        </AccordionMenu>
      </div>
    </Stack>
  ) : (
    <UserAvatar />
  );
};

export default AccountDropdown;
